import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';

interface Beneficiario {
  id: string; nombre: string; parentesco: string;
  curp: string; estado: string; iniciales: string;
}

interface NuevoBen {
  nombre: string; parentesco: string; curp: string;
}

interface Props {
  user: { nombre: string; planNombre: string; plan: string };
  memberId: string;
  openAddSignal?: number;
}

const PARENTESCOS = ['Cónyuge', 'Hijo/a', 'Padre', 'Madre', 'Hermano/a', 'Abuelo/a', 'Otro familiar'];

export default function ScreenFamilia({ user, memberId, openAddSignal = 0 }: Props) {
  const [bens, setBens] = useState<Beneficiario[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [nuevo, setNuevo] = useState<NuevoBen>({ nombre: '', parentesco: '', curp: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const maxBens = user.plan === 'familiar' ? 2 : 3;

  const loadBens = () => {
    if (!memberId || memberId === 'admin') { setLoading(false); return; }
    supabase.from('beneficiarios').select('*').eq('member_id', memberId).then(({ data }) => {
      if (data) {
        setBens(data.map(b => ({
          id: b.id, nombre: b.nombre, parentesco: b.parentesco || '',
          curp: b.curp || '', estado: b.estado || 'carencia',
          iniciales: b.nombre.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase(),
        })));
      }
      setLoading(false);
    });
  };

  useEffect(() => { loadBens(); }, [memberId]);

  useEffect(() => {
    if (openAddSignal > 0) {
      setShowForm(true);
      setError('');
    }
  }, [openAddSignal]);

  const handleAgregar = async () => {
    if (!nuevo.nombre.trim()) { setError('El nombre es requerido'); return; }
    if (!nuevo.parentesco) { setError('Selecciona un parentesco'); return; }
    setSaving(true); setError('');
    const fechaCarencia = new Date();
    fechaCarencia.setDate(fechaCarencia.getDate() + 90);
    const { error: err } = await supabase.from('beneficiarios').insert({
      member_id: memberId,
      nombre: nuevo.nombre.trim(),
      parentesco: nuevo.parentesco,
      curp: nuevo.curp.toUpperCase() || null,
      estado: 'carencia',
      fecha_carencia_fin: fechaCarencia.toISOString().split('T')[0],
    });
    if (err) {
      setError(err.message.includes('CURP') ? 'Esta CURP ya está registrada' : 'Error al agregar. Intenta de nuevo.');
    } else {
      setSuccess('Beneficiario agregado correctamente');
      setNuevo({ nombre: '', parentesco: '', curp: '' });
      setShowForm(false);
      loadBens();
      setTimeout(() => setSuccess(''), 3000);
    }
    setSaving(false);
  };

  return (
    <div className="min-h-screen bg-[#F7F6FF] px-5 py-8">
      {/* Header */}
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#0d0520]">Mi Familia</h1>
          <p className="text-sm text-gray-500 mt-1">Plan {user.planNombre} — {bens.length}/{maxBens} beneficiarios</p>
        </div>
        {bens.length < maxBens && (
          <button
            onClick={() => { setShowForm(!showForm); setError(''); }}
            className="flex items-center gap-1.5 px-4 py-2 rounded-full text-white text-sm font-semibold"
            style={{ backgroundColor: '#534AB7' }}
          >
            <i className="ri-user-add-line" />
            Agregar
          </button>
        )}
      </div>

      {/* Mensaje de éxito */}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 mb-4 flex items-center gap-2">
          <i className="ri-checkbox-circle-line text-green-500" />
          <p className="text-green-700 text-sm">{success}</p>
        </div>
      )}

      {/* Formulario agregar beneficiario */}
      {showForm && (
        <div className="bg-white rounded-2xl p-5 border border-[#534AB7]/30 mb-5">
          <h3 className="font-bold text-[#0d0520] text-base mb-4">Nuevo beneficiario</h3>
          <div className="flex flex-col gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Nombre completo *</label>
              <input
                type="text"
                placeholder="Nombre completo"
                value={nuevo.nombre}
                onChange={e => setNuevo(p => ({ ...p, nombre: e.target.value }))}
                className="w-full border rounded-xl px-4 py-3 text-sm outline-none focus:border-[#534AB7]"
                style={{ borderColor: '#E5E7EB' }}
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Parentesco *</label>
              <select
                value={nuevo.parentesco}
                onChange={e => setNuevo(p => ({ ...p, parentesco: e.target.value }))}
                className="w-full border rounded-xl px-4 py-3 text-sm outline-none focus:border-[#534AB7]"
                style={{ borderColor: '#E5E7EB' }}
              >
                <option value="">Selecciona parentesco</option>
                {PARENTESCOS.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">CURP (opcional)</label>
              <input
                type="text"
                placeholder="CURP del beneficiario"
                value={nuevo.curp}
                onChange={e => setNuevo(p => ({ ...p, curp: e.target.value.toUpperCase() }))}
                maxLength={18}
                className="w-full border rounded-xl px-4 py-3 text-sm outline-none focus:border-[#534AB7] font-mono"
                style={{ borderColor: '#E5E7EB' }}
              />
            </div>
            {error && <p className="text-red-500 text-xs">{error}</p>}
            <div className="flex gap-3 mt-1">
              <button
                onClick={() => { setShowForm(false); setError(''); }}
                className="flex-1 py-3 rounded-xl border text-sm font-semibold text-gray-500"
                style={{ borderColor: '#E5E7EB' }}
              >Cancelar</button>
              <button
                onClick={handleAgregar}
                disabled={saving}
                className="flex-1 py-3 rounded-xl text-white text-sm font-semibold"
                style={{ backgroundColor: '#534AB7' }}
              >{saving ? 'Guardando...' : 'Agregar beneficiario'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Lista de beneficiarios */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-2 border-[#534AB7] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : bens.length === 0 ? (
        <div className="bg-white rounded-2xl p-8 text-center border border-gray-100">
          <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#F3F0FF' }}>
            <i className="ri-user-add-line text-[#534AB7] text-2xl" />
          </div>
          <p className="text-gray-500 text-sm font-medium">No tienes beneficiarios registrados</p>
          <p className="text-gray-400 text-xs mt-1">Puedes agregar hasta {maxBens} personas a tu plan</p>
        </div>
      ) : (
        <div className="space-y-3">
          {bens.map(b => (
            <div key={b.id} className="bg-white rounded-2xl p-4 border border-gray-100 flex items-center gap-4">
              <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
                style={{ backgroundColor: '#534AB7' }}>
                {b.iniciales}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-[#0d0520] text-sm capitalize">{b.nombre}</p>
                <p className="text-gray-400 text-xs">{b.parentesco}</p>
              </div>
              <span className="text-xs font-semibold px-3 py-1 rounded-full"
                style={{ backgroundColor: b.estado === 'activa' ? '#DCFCE7' : '#FEF9C3', color: b.estado === 'activa' ? '#166534' : '#854D0E' }}>
                {b.estado === 'activa' ? 'Activo' : 'Carencia'}
              </span>
            </div>
          ))}

          {/* Espacios disponibles */}
          {bens.length < maxBens && Array.from({ length: maxBens - bens.length }).map((_, i) => (
            <button key={i} onClick={() => setShowForm(true)}
              className="w-full bg-white rounded-2xl p-4 border-2 border-dashed border-gray-200 flex items-center gap-4 hover:border-[#534AB7]/40 transition-colors">
              <div className="w-12 h-12 rounded-full flex items-center justify-center bg-gray-100 flex-shrink-0">
                <i className="ri-user-add-line text-gray-400 text-xl" />
              </div>
              <div className="text-left">
                <p className="text-gray-400 text-sm">Espacio disponible</p>
                <p className="text-[#534AB7] text-xs font-semibold">+ Agregar beneficiario</p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
